import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import KeyIcon from '@mui/icons-material/Key';
import { useApiKey } from './hooks/useApiKey';

const mcpUrl = `${window.location.origin}/mcp`;

const DESKTOP_CONFIG_TEMPLATE = (key: string) =>
  JSON.stringify(
    {
      mcpServers: {
        voltbot: {
          type: 'http',
          url: mcpUrl,
          headers: { 'X-Api-Key': key }
        }
      }
    },
    null,
    2
  );

const CLI_COMMAND = (key: string) =>
  `claude mcp add --transport http voltbot ${mcpUrl} --header "X-Api-Key: ${key}"`;

const DESKTOP_CONFIG_PATH_MAC =
  '~/Library/Application Support/Claude/claude_desktop_config.json';
const DESKTOP_CONFIG_PATH_WIN =
  'C:\\Users\\<username>\\AppData\\Roaming\\Claude\\claude_desktop_config.json';
const DESKTOP_CONFIG_PATH_WIN_BASH =
  '/c/Users/<username>/AppData/Roaming/Claude/claude_desktop_config.json';

type CopyKey = 'url' | 'config' | 'cli' | 'plaintext';

const CodeBlock = ({
  value,
  copyKey,
  copied,
  onCopy
}: {
  value: string;
  copyKey: CopyKey;
  copied: CopyKey | null;
  onCopy: (key: CopyKey, value: string) => void;
}) => (
  <Box
    sx={{
      position: 'relative',
      bgcolor: 'grey.900',
      borderRadius: 1,
      p: 1.5,
      pr: 5,
      fontFamily: 'monospace',
      fontSize: '0.8rem',
      color: 'grey.100',
      whiteSpace: 'pre',
      overflowX: 'auto'
    }}
  >
    {value}
    <Tooltip title={copied === copyKey ? 'Copied!' : 'Copy'}>
      <IconButton
        size="small"
        onClick={() => onCopy(copyKey, value)}
        sx={{
          position: 'absolute',
          top: 6,
          right: 6,
          color: copied === copyKey ? 'success.light' : 'grey.400',
          '&:hover': { color: 'white' }
        }}
      >
        {copied === copyKey ? (
          <CheckIcon fontSize="small" />
        ) : (
          <ContentCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  </Box>
);

const Section = ({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export const McpSetupCard = () => {
  const [copied, setCopied] = useState<CopyKey | null>(null);
  const [revealDialogOpen, setRevealDialogOpen] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);

  const { metadata, isLoading, generate, revoke, isGenerating, isRevoking } =
    useApiKey();

  const handleCopy = (key: CopyKey, value: string) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleGenerate = async () => {
    const result = await generate();
    if (result?.plaintextKey) {
      setNewKey(result.plaintextKey);
      setRevealDialogOpen(true);
    }
  };

  const handleRevoke = async () => {
    await revoke();
  };

  const handleCloseReveal = () => {
    setRevealDialogOpen(false);
    setNewKey(null);
  };

  const displayKey = metadata?.keyPrefix ?? 'No key generated';
  const configKey = metadata?.keyPrefix
    ? `${metadata.keyPrefix}<your-full-key>`
    : '<your-api-key>';

  return (
    <>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <SmartToyIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Claude AI Integration (MCP)
            </Typography>
            <Chip
              label="Beta"
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Connect Claude Desktop or Claude Code to this VoltBot instance. Once
            connected, Claude can read, create, and update scripts on your
            behalf using natural language.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {/* API Key Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <KeyIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Your API Key
            </Typography>
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            </Box>
          ) : metadata ? (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  px: 1.5,
                  py: 1
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', flexGrow: 1 }}
                >
                  {displayKey}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Created {new Date(metadata.createdAt).toLocaleDateString()} ·{' '}
                {metadata.lastUsedAt
                  ? `Last used ${new Date(metadata.lastUsedAt).toLocaleDateString()}`
                  : 'Never used'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleGenerate}
                  disabled={isGenerating || isRevoking}
                >
                  {isGenerating ? 'Regenerating...' : 'Regenerate'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={handleRevoke}
                  disabled={isGenerating || isRevoking}
                >
                  {isRevoking ? 'Revoking...' : 'Revoke'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                Generate an API key to authenticate Claude Code and Claude
                Desktop with this VoltBot instance.
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={handleGenerate}
                disabled={isGenerating}
                startIcon={
                  isGenerating ? <CircularProgress size={14} /> : undefined
                }
              >
                {isGenerating ? 'Generating...' : 'Generate Key'}
              </Button>
            </Box>
          )}

          <Divider sx={{ mb: 3 }} />

          <Section title="Your MCP Server URL">
            <CodeBlock
              value={mcpUrl}
              copyKey="url"
              copied={copied}
              onCopy={handleCopy}
            />
          </Section>

          <Accordion
            disableGutters
            elevation={0}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Claude Code (CLI)
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Run this command in your terminal, replacing{' '}
                <code>&lt;your-full-key&gt;</code> with your actual API key.
              </Typography>
              <CodeBlock
                value={CLI_COMMAND(configKey)}
                copyKey="cli"
                copied={copied}
                onCopy={handleCopy}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            elevation={0}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Claude Desktop App
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Open your Claude Desktop config file and add the following,
                replacing <code>&lt;your-full-key&gt;</code> with your actual
                API key. Save the file, then restart Claude Desktop.
              </Typography>

              <Section title="Config file location">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box>
                    <Chip label="Mac" size="small" sx={{ mr: 1, mb: 0.5 }} />
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                    >
                      {DESKTOP_CONFIG_PATH_MAC}
                    </Typography>
                  </Box>
                  <Box>
                    <Chip
                      label="Windows (Explorer)"
                      size="small"
                      sx={{ mr: 1, mb: 0.5 }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                    >
                      {DESKTOP_CONFIG_PATH_WIN}
                    </Typography>
                  </Box>
                  <Box>
                    <Chip
                      label="Windows (Git Bash)"
                      size="small"
                      sx={{ mr: 1, mb: 0.5 }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                    >
                      {DESKTOP_CONFIG_PATH_WIN_BASH}
                    </Typography>
                  </Box>
                </Box>
              </Section>

              <Section title="claude_desktop_config.json">
                <CodeBlock
                  value={DESKTOP_CONFIG_TEMPLATE(configKey)}
                  copyKey="config"
                  copied={copied}
                  onCopy={handleCopy}
                />
              </Section>

              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  If you already have an <code>mcpServers</code> section, just
                  add the <code>voltbot</code> entry inside it — don&apos;t
                  replace the whole file.
                </Typography>
              </Alert>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {/* One-time key reveal dialog */}
      <Dialog
        open={revealDialogOpen}
        onClose={handleCloseReveal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Your API Key</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key now — it won&apos;t be shown again.
          </Alert>
          <CodeBlock
            value={newKey ?? ''}
            copyKey="plaintext"
            copied={copied}
            onCopy={handleCopy}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReveal} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
